import type { GraphQLCarDetail } from '~/types/graphql';

export type VehicleTextTab = {
  label: string;
  value: 'vehicle-data' | 'equipment';
  content: string;
};

/**
 * Builds the HTML for the vehicle-detail text tabs (description + equipment).
 * Shared by the on-screen tabbed view (TextContents) and the print view so the
 * two never drift apart.
 */
export function useVehicleTextContent(carDetail?: GraphQLCarDetail): { tabs: VehicleTextTab[] } {
  const { t } = useI18n();

  // The sync splits Motiondata's combined text into `highlights` (equipment) and
  // `description` (prose). Vehicles synced before that shipped still carry both
  // halves concatenated in `description`, so fall back to carving them apart
  // in-page until every vehicle has been re-synced.
  const isSplitBySync = Boolean(carDetail?.highlights);

  const generateDescriptionContent = () => {
    const description = isSplitBySync
      ? carDetail?.description
      : carDetail?.features_description || carDetail?.description?.split('...<br /><br />').filter(Boolean)?.[1];

    let text = '';

    if (description) {
      text += `
        <h2 class="h5 mb-4 text-xl">${t('components.carDetail.textContents.vehicle-data.title')}</h2>
        <div class="text-regular-normal mb-5">${description}</div>
        `;
    }

    text += `
        <h2 class="h5 mb-4 text-xl">${t('components.carDetail.textContents.vehicle-data.label')}</h2>
        <table class="w-full border-collapse border-l border-l-[#EB5E0E80]">
          <tbody>
            <tr class="border-b border-b-neutral-lightest">
              <td class="p-4 text-regular-semibold border-r border-r-neutral-lightest">${t('components.carDetail.textContents.vehicle-data.bodyStyle')}</td>
              <td class="p-4 text-regular-normal">${carDetail?.body_style?.[0]?.name}</td>
            </tr>
            <tr class="border-b border-b-neutral-lightest">
              <td class="p-4 text-regular-semibold border-r border-r-neutral-lightest">${t('components.carDetail.textContents.vehicle-data.color')}</td>
              <td class="p-4 text-regular-normal">${carDetail?.exterior_color}</td>
            </tr>
            <tr class="border-b border-b-neutral-lightest">
              <td class="p-4 text-regular-semibold border-r border-r-neutral-lightest">${t('components.carDetail.textContents.vehicle-data.co2')}</td>
              <td class="p-4 text-regular-normal">${carDetail?.co2_g_km || 0} g/km</td>
            </tr>
            <tr class="border-b border-b-neutral-lightest">
              <td class="p-4 text-regular-semibold border-r border-r-neutral-lightest">${t('components.carDetail.textContents.vehicle-data.displacement')}</td>
              <td class="p-4 text-regular-normal">${carDetail?.displacement_cm3 || 0} cm³</td>
            </tr>
            <tr class="border-b border-b-neutral-lightest">
              <td class="p-4 text-regular-semibold border-r border-r-neutral-lightest">${t('components.carDetail.textContents.vehicle-data.seats')}</td>
              <td class="p-4 text-regular-normal">${carDetail?.seats || 0}</td>
            </tr>
          </tbody>
        </table>
      `;

    return text;
  };

  const generateEquipmentContent = () => {
    const highlights = isSplitBySync
      ? carDetail?.highlights
      : carDetail?.description?.split('\n').filter(Boolean)?.[0];

    let text = '';
    if (highlights) {
      text += `
        <h5 class="mb-4 text-xl">${t('components.carDetail.textContents.equipment.highlihgtsTitle')}</h5>
        <div class="text-regular-normal mb-5">${highlights}</div>
        `;
    }

    if (carDetail?.features?.length) {
      text += `
        <h5 class="mb-4 text-xl">${t('components.carDetail.textContents.equipment.label')}</h5>
        <ul class="!list-none text-regular-normal columns-1 sm:columns-2 md:columns-1 lg:columns-2">
        ${carDetail?.features?.map(feature => `<li class="flex items-start gap-2"><span class="size-1 min-w-1 mt-[10px] rounded-full bg-primary"></span><span>${feature?.name}</span></li>`).join('')}
        </ul>
        `;
    }

    return text;
  };

  const tabs: VehicleTextTab[] = [
    {
      label: t('components.carDetail.textContents.vehicle-data.label'),
      value: 'vehicle-data',
      content: generateDescriptionContent(),
    },
    {
      label: t('components.carDetail.textContents.equipment.label'),
      value: 'equipment',
      content: generateEquipmentContent(),
    },
  ];

  return { tabs };
}
